import { useState, useEffect, useRef } from "react";
import { logger } from "@/lib/logger";
import { useToast } from "@/hooks/use-toast";
import { useSuggestedPrompts } from "@/hooks/use-suggested-prompts";
import { apiClient } from "@/lib/api-client";
import { handleApiError } from "@/lib/error-handling";
import type { SleepEvent, Child, SleepGoal } from "@/lib/types";
import { getAICoachTemporalContext, formatMessageTimeForAI } from "@/lib/coach-context";
import { AppReview } from '@capawesome/capacitor-app-review';
import { trackEvent, AnalyticsEvents } from '@/lib/analytics';
import {
    incrementMessagesSentCount,
    getLastReviewRequestTime,
    setLastReviewRequestTime,
    getFreeTrialMessagesRemaining,
    incrementFreeTrialMessagesUsed,
    resetFreeTrialMessages,
    FREE_TRIAL_LIMIT
} from "@/lib/storage";

export interface Message {
    role: 'user' | 'model';
    content: string;
    timestamp: Date;
    failed?: boolean;
    errorMessage?: string;
    suggestedGoals?: SleepGoal;
    applied?: boolean;
    bookmarked?: boolean;
}

interface UseChatProps {
    events: SleepEvent[];
    child?: Child | null;
    goal?: SleepGoal;
    isSubscribed: boolean;
    onUpdateGoal?: (newGoal: Partial<SleepGoal>) => Promise<void>;
}

export function useChat({ events, child, goal, isSubscribed, onUpdateGoal }: UseChatProps) {
    const [messages, setMessages] = useState<Message[]>([]);
    const [input, setInput] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const [freeTrialMessagesRemaining, setFreeTrialMessagesRemaining] = useState(() => getFreeTrialMessagesRemaining());
    const [isFreeTrialExhausted, setIsFreeTrialExhausted] = useState(() => getFreeTrialMessagesRemaining() === 0);
    const scrollAreaRef = useRef<HTMLDivElement>(null);
    const lastMessageRef = useRef<HTMLDivElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const isWaitingRef = useRef(false);
    const isLoadingRef = useRef(false);
    const pendingUserMessageRef = useRef<{ content: string; timestamp: number; retryIndex?: number } | null>(null);
    const pollingTimerRef = useRef<NodeJS.Timeout | null>(null);
    const pollingStartTimeRef = useRef<number>(0);
    const { toast } = useToast();

    // Keep isLoadingRef synchronized with state
    useEffect(() => {
        isLoadingRef.current = isLoading;
    }, [isLoading]);

    const stopPolling = () => {
        if (pollingTimerRef.current) {
            clearTimeout(pollingTimerRef.current);
            pollingTimerRef.current = null;
        }
        pollingStartTimeRef.current = 0;
    };

    const handleModelResponseSuccess = (
        modelMsg: Message,
        allMessages: Message[],
        retryIndex?: number
    ) => {
        stopPolling();
        isWaitingRef.current = false;
        pendingUserMessageRef.current = null;
        setIsLoading(false);
        setMessages(allMessages);

        // Track successful response
        trackEvent(AnalyticsEvents.AI_COACH_RESPONSE_RECEIVED, {
            has_suggested_goals: !!modelMsg.suggestedGoals,
        });

        // Increment free trial usage counter for unsubscribed users
        if (!isSubscribed && retryIndex === undefined) {
            const newUsed = incrementFreeTrialMessagesUsed();
            const remainingMsgs = Math.max(0, FREE_TRIAL_LIMIT - newUsed);
            setFreeTrialMessagesRemaining(remainingMsgs);
            if (remainingMsgs === 0) {
                setIsFreeTrialExhausted(true);
                trackEvent(AnalyticsEvents.FREE_TRIAL_EXHAUSTED);
            }
        }
    };

    const markPendingMessageFailed = (errorMessage: string, retryIndex?: number) => {
        const pending = pendingUserMessageRef.current;
        pendingUserMessageRef.current = null;
        isWaitingRef.current = false;
        setIsLoading(false);

        trackEvent(AnalyticsEvents.AI_COACH_RESPONSE_FAILED);

        setMessages(prev => {
            const updated = [...prev];
            const failedMessage: Message = {
                role: 'user',
                content: pending ? pending.content : (updated[updated.length - 1]?.content || ''),
                timestamp: pending ? new Date(pending.timestamp) : new Date(),
                failed: true,
                errorMessage: errorMessage || 'Unable to get AI response. Please try again.'
            };

            if (retryIndex !== undefined && retryIndex >= 0 && retryIndex < updated.length) {
                updated[retryIndex] = failedMessage;
                return updated;
            } else if (updated.length > 0 && updated[updated.length - 1].role === 'user') {
                updated[updated.length - 1] = failedMessage;
                return updated;
            } else {
                return [...updated, failedMessage];
            }
        });
    };

    const startPolling = (childId: string, retryIndex?: number) => {
        stopPolling();
        pollingStartTimeRef.current = Date.now();

        const poll = async () => {
            if (!child || child.id !== childId || !isWaitingRef.current) {
                stopPolling();
                return;
            }

            // Timeout after 90 seconds
            if (Date.now() - pollingStartTimeRef.current > 90000) {
                stopPolling();
                isWaitingRef.current = false;
                setIsLoading(false);
                markPendingMessageFailed('Response timed out. Please try again.', retryIndex);
                return;
            }

            try {
                const response = await apiClient.getConversation(childId);
                if (response.success && response.data && Array.isArray(response.data.messages) && response.data.messages.length > 0) {
                    const serverMessages: Message[] = response.data.messages.map((msg: any) => ({
                        ...msg,
                        timestamp: new Date(msg.timestamp)
                    }));
                    const lastMsg = serverMessages[serverMessages.length - 1];

                    if (lastMsg && lastMsg.role === 'model') {
                        handleModelResponseSuccess(lastMsg, serverMessages, retryIndex);
                        return;
                    }

                    if (response.data.lastError) {
                        stopPolling();
                        isWaitingRef.current = false;
                        setIsLoading(false);
                        markPendingMessageFailed(response.data.lastError, retryIndex);
                        return;
                    }
                }
            } catch (err) {
                console.warn('Error polling for coach response:', err);
            }

            pollingTimerRef.current = setTimeout(poll, 2000);
        };

        pollingTimerRef.current = setTimeout(poll, 2000);
    };

    const recoverOrPoll = async (childId: string) => {
        if (!child || child.id !== childId) return false;
        if (abortControllerRef.current?.signal.aborted) return false;

        try {
            const response = await apiClient.getConversation(childId);
            if (response.success && response.data && Array.isArray(response.data.messages) && response.data.messages.length > 0) {
                const serverMessages: Message[] = response.data.messages.map((msg: any) => ({
                    ...msg,
                    timestamp: new Date(msg.timestamp)
                }));
                const lastMsg = serverMessages[serverMessages.length - 1];

                if (lastMsg && lastMsg.role === 'model') {
                    const pending = pendingUserMessageRef.current;
                    const modelTime = lastMsg.timestamp ? new Date(lastMsg.timestamp).getTime() : 0;
                    if (!pending || modelTime >= pending.timestamp - 5000) {
                        handleModelResponseSuccess(lastMsg, serverMessages, pending?.retryIndex);
                        return true;
                    }
                }

                const isGenerating = response.data.isGenerating === true;
                const timeSinceLast = lastMsg?.timestamp ? Date.now() - new Date(lastMsg.timestamp).getTime() : 0;
                if ((isGenerating || (lastMsg?.role === 'user' && timeSinceLast < 90000)) && !response.data.lastError) {
                    setIsLoading(true);
                    isWaitingRef.current = true;
                    startPolling(childId, pendingUserMessageRef.current?.retryIndex);
                    return true;
                }
            }
        } catch (err) {
            console.warn('Error recovering conversation:', err);
        }
        return false;
    };

    // Free trial mode: unsubscribed users who still have free messages remaining
    const isFreeTrialMode = !isSubscribed && !isFreeTrialExhausted;

    // Reset free trial exhausted state when user subscribes
    useEffect(() => {
        if (isSubscribed && isFreeTrialExhausted) {
            resetFreeTrialMessages();
            setIsFreeTrialExhausted(false);
            setFreeTrialMessagesRemaining(FREE_TRIAL_LIMIT);
        }
    }, [isSubscribed, isFreeTrialExhausted]);

    // Suggested prompts hook
    const { suggestedPrompts, isLoadingPrompts, generatePrompts, clearPrompts } = useSuggestedPrompts(events, child, messages, goal);

    // Reset initialization when child changes
    useEffect(() => {
        if (child) {
            setIsInitialized(false);
            setMessages([]);
        }
    }, [child?.id]);

    // Load conversation from Firestore when child changes
    useEffect(() => {
        if (child && !isInitialized) {
            const loadConversation = async () => {
                // Load real conversations for all users (including free trial users)
                try {
                    const response = await apiClient.getConversation(child.id);
                    if (response.success && response.data && response.data.messages && response.data.messages.length > 0) {
                        // Convert timestamp strings back to Date objects
                        const messagesWithDates = response.data.messages.map((msg: any) => ({
                            ...msg,
                            timestamp: new Date(msg.timestamp)
                        }));
                        setMessages(messagesWithDates);
                    } else {
                        // No saved conversation, add welcome message
                        const welcomeMessage: Message = {
                            role: 'model',
                            content: `Hi there! 👋 I'm your personal AI sleep coach, here to help ${child.name} get the restful sleep they deserve. Whether you're dealing with bedtime battles, early wake-ups, or just want to optimize their sleep schedule — I've got you covered. What's on your mind?`,
                            timestamp: new Date(),
                        };
                        setMessages([welcomeMessage]);
                    }
                    setIsInitialized(true);
                } catch (error) {
                    logger.error('Error loading conversation', { error, childId: child.id });
                    console.error('Error loading conversation:', error);
                    // Fallback to welcome message
                    const welcomeMessage: Message = {
                        role: 'model',
                        content: `Hi there! 👋 I'm your personal AI sleep coach, here to help ${child.name} get the restful sleep they deserve. Whether you're dealing with bedtime battles, early wake-ups, or just want to optimize their sleep schedule — I've got you covered. What's on your mind?`,
                        timestamp: new Date(),
                    };
                    setMessages([welcomeMessage]);
                    setIsInitialized(true);
                }
            };

            loadConversation();
        }
    }, [child, isInitialized, isSubscribed, freeTrialMessagesRemaining]);

    const isInitialMount = useRef(true);

    // Reset initial mount flag when child changes
    useEffect(() => {
        if (child) {
            isInitialMount.current = true;
        }
    }, [child?.id]);

    // Auto-scroll to bottom when initialized or when new messages arrive
    useEffect(() => {
        if (!isInitialized || messages.length === 0) return;

        if (isInitialMount.current) {
            isInitialMount.current = false;
            // Initial mount or tab switch: instant scroll to bottom so the user starts at the most recent messages
            scrollToBottom(false);
            const t1 = setTimeout(() => scrollToBottom(false), 50);
            const t2 = setTimeout(() => scrollToBottom(false), 150);
            const t3 = setTimeout(() => scrollToBottom(false), 300);
            return () => {
                clearTimeout(t1);
                clearTimeout(t2);
                clearTimeout(t3);
            };
        } else {
            // New message sent or received during active conversation: smooth scroll
            scrollToBottom(true);
            const t = setTimeout(() => scrollToBottom(true), 100);
            return () => clearTimeout(t);
        }
    }, [child?.id, isInitialized, messages.length]);

    // Function to scroll to bottom
    const scrollToBottom = (smooth = false) => {
        const behavior: ScrollBehavior = smooth ? 'smooth' : 'auto';

        // 1. Scroll Radix ScrollArea viewport if present
        if (scrollAreaRef.current) {
            const viewport = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]') as HTMLElement;
            if (viewport) {
                if (typeof viewport.scrollTo === 'function') {
                    viewport.scrollTo({ top: viewport.scrollHeight, behavior });
                } else {
                    viewport.scrollTop = viewport.scrollHeight;
                }
            }
        }

        // 2. Scroll the last message into view
        if (lastMessageRef.current && typeof lastMessageRef.current.scrollIntoView === 'function') {
            lastMessageRef.current.scrollIntoView({ behavior, block: 'end' });
        }

        // 3. If window/document is the scroll container, scroll window to bottom so latest messages and input bar are in view
        if (typeof window !== 'undefined') {
            const docHeight = Math.max(
                document.body?.scrollHeight || 0,
                document.documentElement?.scrollHeight || 0
            );
            if (docHeight > window.innerHeight) {
                if (typeof window.scrollTo === 'function') {
                    window.scrollTo({ top: docHeight, behavior });
                }
            }
        }
    };

    const calculateAge = (birthMonth: number, birthYear: number) => {
        const today = new Date();
        const birthDate = new Date(birthYear, birthMonth - 1);
        let ageYears = today.getFullYear() - birthDate.getFullYear();
        let ageMonths = today.getMonth() - birthDate.getMonth();

        if (ageMonths < 0 || (ageMonths === 0 && today.getDate() < birthDate.getDate())) {
            ageYears--;
            ageMonths += 12;
        }

        return ageYears * 12 + ageMonths;
    };

    const handleSendMessage = async (messageContent?: string, retryIndex?: number) => {
        const content = messageContent || input.trim();
        if (!content || isLoading || !child) return;

        const userMessage: Message = {
            role: 'user',
            content: content,
            timestamp: new Date(),
        };

        let updatedMessages: Message[];

        // If retrying, replace the message at the retry index
        if (retryIndex !== undefined) {
            updatedMessages = [...messages];
            updatedMessages[retryIndex] = userMessage;
        } else {
            // Otherwise, append new message
            updatedMessages = [...messages, userMessage];
        }

        clearPrompts();
        setMessages(updatedMessages);
        if (!messageContent) {
            setInput("");
        }
        isWaitingRef.current = true;
        pendingUserMessageRef.current = {
            content,
            timestamp: Date.now(),
            retryIndex
        };
        setIsLoading(true);

        // Track message sent event
        trackEvent(AnalyticsEvents.AI_COACH_MESSAGE_SENT, {
            is_subscribed: isSubscribed,
            is_suggested_prompt: !!messageContent && messageContent !== input.trim(),
        });

        const controller = new AbortController();
        abortControllerRef.current = controller;

        // Save conversation to Firestore (setting isGenerating: true)
        try {
            await apiClient.saveConversation(child.id, updatedMessages, true);
        } catch (error) {
            console.error('Error saving conversation:', error);
            // Non-blocking error, just log it
            logger.error('Error saving conversation', { error, childId: child.id });
        }

        try {
            // Track message count for app review prompt (only for subscribed users)
            if (isSubscribed && retryIndex === undefined) {
                const count = incrementMessagesSentCount();

                // Review Prompt Strategy:
                // 1. Engagement: User must have sent at least 5 messages total.
                // 2. Cooldown: We prompt randomly or periodically, max 3x a year (approx every 4 months).

                const REVIEW_COOLDOWN_MS = 120 * 24 * 60 * 60 * 1000; // ~4 months
                const lastRequestTime = getLastReviewRequestTime();
                const timeSinceLastRequest = Date.now() - lastRequestTime;

                // Trigger if:
                // - User is engaged (>= 5 messages)
                // - AND (Never requested OR Cooldown has passed)
                if (count >= 5 && (lastRequestTime === 0 || timeSinceLastRequest > REVIEW_COOLDOWN_MS)) {
                    // Trigger review prompt
                    console.log('Triggering App Review Prompt (Periodic)');
                    AppReview.requestReview().then(() => {
                        setLastReviewRequestTime(Date.now());
                    }).catch(err => {
                        console.error('Error requesting app review:', err);
                        // Even on error, update time to prevent spamming on every message if the API is failing or unsupported
                        setLastReviewRequestTime(Date.now());
                    });
                }
            }

            // Build real-time temporal context (current time, wake window status, chronological sleep logs)
            const now = new Date();
            const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
            const temporalContext = getAICoachTemporalContext(events, now, goal, timezone);

            // Include the current user message in the conversation history, limited to last 15 messages
            const allMessages = [...messages, userMessage];
            const limitedMessages = allMessages.slice(-15); // Keep only last 15 messages
            const conversationHistory = limitedMessages.map(msg => {
                const msgDate = msg.timestamp instanceof Date ? msg.timestamp : (msg.timestamp ? new Date(msg.timestamp) : undefined);
                const formattedTimestamp = msgDate && !isNaN(msgDate.getTime())
                    ? formatMessageTimeForAI(msgDate, now, timezone)
                    : undefined;
                return {
                    role: msg.role,
                    content: [{ text: msg.content }],
                    timestamp: formattedTimestamp
                };
            });

            // Determine last message date prior to the current user message
            const priorMsg = messages.length > 0 ? messages[messages.length - 1] : null;
            const lastMessageDate = priorMsg?.timestamp
                ? (priorMsg.timestamp instanceof Date ? priorMsg.timestamp.toISOString() : new Date(priorMsg.timestamp).toISOString())
                : undefined;

            const response = await apiClient.analyzeSleepPatterns(
                {
                    sleepData: temporalContext.formattedSleepData,
                    childInfo: `${child.name} (${child.gender}, ${calculateAge(child.birthMonth, child.birthYear)} months old)`,
                    history: conversationHistory,
                    lastMessageDate,
                    sleepGoals: goal,
                    childId: child.id,
                    userId: child.userId,
                    currentTime: temporalContext.currentTime,
                    timezone: temporalContext.timezone,
                    currentTimeFormatted: temporalContext.currentTimeFormatted,
                    currentStatusSummary: temporalContext.currentStatusSummary
                },
                controller.signal
            );

            if (response.cancelled) {
                stopPolling();
                isWaitingRef.current = false;
                pendingUserMessageRef.current = null;
                setIsLoading(false);
                return;
            }

            if (response.success && response.data) {
                const aiMessage: Message = {
                    role: 'model',
                    content: response.data.response,
                    timestamp: new Date(),
                    suggestedGoals: response.data.suggestedGoals,
                };

                let finalMessages: Message[];
                if (retryIndex !== undefined) {
                    // For retries, the user message is already at retryIndex
                    finalMessages = [...updatedMessages, aiMessage];
                } else {
                    // For new messages, append both user message and AI response
                    finalMessages = [...messages, userMessage, aiMessage];
                }

                handleModelResponseSuccess(aiMessage, finalMessages, retryIndex);

                // Save conversation to Firestore with isGenerating: false
                try {
                    await apiClient.saveConversation(child.id, finalMessages, false);
                } catch (error) {
                    console.error('Error saving conversation:', error);
                }
            } else {
                // If the HTTP response returned an error, check if the server finished or is processing in the background
                const recovered = await recoverOrPoll(child.id);
                if (!recovered) {
                    markPendingMessageFailed(
                        response.error || 'Unable to get AI response. The service might be temporarily unavailable.',
                        retryIndex
                    );
                }
            }
        } catch (error) {
            console.error('Error getting AI response:', error);
            logger.error('Error getting AI response', {
                error,
                childId: child.id,
                messageCount: messages.length,
                lastUserMessage: userMessage.content
            });

            if (error instanceof Error && error.name === 'AbortError' && abortControllerRef.current === null) {
                stopPolling();
                isWaitingRef.current = false;
                pendingUserMessageRef.current = null;
                setIsLoading(false);
                return;
            }

            // Check if backend actually finished or is still generating in background (e.g. app switch or network reconnect)
            const recovered = await recoverOrPoll(child.id);
            if (!recovered) {
                markPendingMessageFailed(
                    'Message failed to send. This could be due to a timeout or server overload.',
                    retryIndex
                );
            }
        } finally {
            if (abortControllerRef.current === controller) {
                abortControllerRef.current = null;
            }
        }
    };

    const handleRetryMessage = async (messageIndex: number) => {
        const failedMessage = messages[messageIndex];
        if (!failedMessage || !child || isLoading) return;

        // Send the message directly with its content, replacing it at the same index
        await handleSendMessage(failedMessage.content, messageIndex);
    };

    const handleCancelResponse = () => {
        stopPolling();
        isWaitingRef.current = false;
        pendingUserMessageRef.current = null;
        if (abortControllerRef.current) {
            if (typeof DOMException !== 'undefined') {
                abortControllerRef.current.abort(new DOMException('Cancelled by user', 'AbortError'));
            } else {
                abortControllerRef.current.abort('user-cancelled');
            }
            abortControllerRef.current = null;
        }
        setIsLoading(false);
    };

    const clearConversation = async () => {
        stopPolling();
        isWaitingRef.current = false;
        pendingUserMessageRef.current = null;
        if (child) {
            try {
                await apiClient.deleteConversation(child.id);
            } catch (error) {
                console.error('Error deleting conversation:', error);
            }
        }

        // Clear messages and reset initialization
        setMessages([]);
        setIsInitialized(false);

        trackEvent(AnalyticsEvents.AI_COACH_CONVERSATION_CLEARED);
    };

    const handleSuggestedPrompt = (prompt: string) => {
        setInput(prompt);
    };

    const handleGeneratePrompts = async () => {
        try {
            await generatePrompts();
            trackEvent(AnalyticsEvents.AI_COACH_PROMPT_GENERATED);
        } catch (error) {
            console.error('Error generating suggested prompts:', error);
            // Show user-friendly message (don't expose server error details)
            handleApiError('Unable to generate suggested prompts. Please try again.', toast, error);
        }
    };

    const handleUpdateGoals = async (newGoals: SleepGoal, messageIndex?: number) => {
        if (!child) return;

        try {
            if (onUpdateGoal) {
                // Use the provided callback which handles state updates
                await onUpdateGoal(newGoals);
            } else {
                // Fallback to direct API call (no local state update)
                await apiClient.updateGoal({
                    ...goal, // Keep existing ID and other fields
                    ...newGoals,
                    childId: child.id,
                    userId: child.userId,
                } as SleepGoal);
            }

            toast({
                title: "Goals Updated",
                description: "The sleep schedule has been updated based on the coach's suggestion.",
            });

            trackEvent(AnalyticsEvents.AI_COACH_GOAL_APPLIED);

            // Mark message as applied if index provided
            if (messageIndex !== undefined) {
                setMessages(prev => {
                    const updatedMessages = [...prev];
                    if (updatedMessages[messageIndex]) {
                        updatedMessages[messageIndex] = {
                            ...updatedMessages[messageIndex],
                            applied: true
                        };

                        // Update conversation in Firestore
                        if (child) {
                            apiClient.saveConversation(child.id, updatedMessages).catch(console.error);
                        }
                    }
                    return updatedMessages;
                });
            }
        } catch (error) {
            console.error('Error updating goals:', error);
            toast({
                title: "Update Failed",
                description: "Could not update sleep goals. Please try again.",
                variant: "destructive"
            });
        }
    };

    const handleToggleBookmark = async (messageIndex: number) => {
        if (messageIndex < 0 || messageIndex >= messages.length) return;

        const message = messages[messageIndex];
        // Only allow bookmarking AI messages
        if (message.role !== 'model') return;

        setMessages(prev => {
            const updatedMessages = [...prev];
            if (updatedMessages[messageIndex]) {
                updatedMessages[messageIndex] = {
                    ...updatedMessages[messageIndex],
                    bookmarked: !updatedMessages[messageIndex].bookmarked
                };

                trackEvent(AnalyticsEvents.AI_COACH_BOOKMARK_TOGGLED, {
                    action: updatedMessages[messageIndex].bookmarked ? 'add' : 'remove',
                });

                // Persist to Firestore
                if (child) {
                    apiClient.saveConversation(child.id, updatedMessages).catch(console.error);
                }
            }
            return updatedMessages;
        });
    };

    // Get all bookmarked messages with their indices
    const bookmarkedMessages = messages
        .map((msg, index) => ({ ...msg, originalIndex: index }))
        .filter(msg => msg.bookmarked);

    return {
        messages,
        input,
        setInput,
        isLoading,
        isInitialized,
        scrollAreaRef,
        lastMessageRef,
        handleSendMessage,
        handleRetryMessage,
        handleCancelResponse,
        clearConversation,
        handleSuggestedPrompt,
        handleGeneratePrompts,
        suggestedPrompts,
        isLoadingPrompts,
        isFreeTrialMode,
        isFreeTrialExhausted,
        freeTrialMessagesRemaining,
        handleUpdateGoals,
        handleToggleBookmark,
        bookmarkedMessages
    };
}
